import { createContext, useContext, useRef, useState } from 'react';
import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import type { Canvas as FabricCanvas, FabricObject } from 'fabric';

import type { ItemType } from '@/config/itemTypes';
import { isDugout } from '@/config/features';
import { FALLBACK_THEME, isTheme, type Theme } from '@/config/theme';
import { DUGOUT_DEFAULT_ICON_TYPE } from '@/config/dugout';

// The Fabric selection is polymorphic: the array of selected objects (a multi-select), a line-type
// variation string (when a line tool is armed), or nothing. Exported so the Editor (which receives
// this value as a prop) shares the exact type.
export type Selection = FabricObject[] | string | null;

export interface ToolContextValue {
  scaleStateRef: RefObject<number>;
  setScale: (scale: number) => void;
  canvasStateRef: RefObject<FabricCanvas>;
  setCanvas: (canvas: FabricCanvas) => void;
  canvasRef: RefObject<HTMLCanvasElement | null> | null;
  setCanvasRef: Dispatch<
    SetStateAction<RefObject<HTMLCanvasElement | null> | null>
  >;
  selection: Selection;
  setSelection: Dispatch<SetStateAction<Selection>>;
  fieldTypes: ItemType[];
  setFieldTypes: Dispatch<SetStateAction<ItemType[]>>;
  fieldDimensions: number[];
  setFieldDimensions: Dispatch<SetStateAction<number[]>>;
  toolTypes: ItemType[];
  setToolTypes: Dispatch<SetStateAction<ItemType[]>>;
  lineTypes: ItemType[];
  setLineTypes: Dispatch<SetStateAction<ItemType[]>>;
  shirtTypes: ItemType[];
  setShirtTypes: Dispatch<SetStateAction<ItemType[]>>;
  goalTypes: ItemType[];
  setGoalTypes: Dispatch<SetStateAction<ItemType[]>>;
  itemTypes: ItemType[];
  setItemTypes: Dispatch<SetStateAction<ItemType[]>>;
  coneTypes: ItemType[];
  setConeTypes: Dispatch<SetStateAction<ItemType[]>>;
  defaultTypes: ItemType[];
  setDefaultTypes: Dispatch<SetStateAction<ItemType[]>>;
  fieldSelected: ItemType | null;
  setFieldSelected: Dispatch<SetStateAction<ItemType | null>>;
  lineSelected: string | null;
  setLineSelected: Dispatch<SetStateAction<string | null>>;
  iconType: string;
  setIconType: Dispatch<SetStateAction<string>>;
  showCancelOverlay: boolean;
  setShowCancelOverlay: Dispatch<SetStateAction<boolean>>;
  isIndoor: boolean;
  setIsIndoor: Dispatch<SetStateAction<boolean>>;
  storeFieldType: string;
  setStoreFieldType: Dispatch<SetStateAction<string>>;
  setStoreLineType: (type: string) => void;
  storeLineTypeStateRef: RefObject<string>;
  storeIconType: string;
  setStoreIconType: Dispatch<SetStateAction<string>>;
  storeSelection: FabricObject | null;
  setStoreSelection: Dispatch<SetStateAction<FabricObject | null>>;
  storeCollection: FabricObject[];
  setStoreCollection: Dispatch<SetStateAction<FabricObject[]>>;
  storePitch: string;
  setStorePitch: Dispatch<SetStateAction<string>>;
  parentUrl: string;
  setParentUrl: Dispatch<SetStateAction<string>>;
  storeAddItem: (item: FabricObject) => void;
  isMenuActive: boolean;
  setIsMenuActive: Dispatch<SetStateAction<boolean>>;
  isMobileStateRef: RefObject<boolean>;
  setIsMobile: (isMobile: boolean) => void;
  enableResetButton: boolean;
  setEnableResetButton: Dispatch<SetStateAction<boolean>>;
  preventClickBeforeScroll: boolean;
  setPreventClickBeforeScroll: Dispatch<SetStateAction<boolean>>;
  isDrawingLine: boolean;
  setIsDrawingLine: Dispatch<SetStateAction<boolean>>;
  isIndoorActive: boolean;
  setIsIndoorActive: Dispatch<SetStateAction<boolean>>;
  drawingType: string | false;
  setDrawingType: Dispatch<SetStateAction<string | false>>;
  reloadField: boolean;
  setReloadField: Dispatch<SetStateAction<boolean>>;
  // The theme resolved for THIS request (proxy.ts → app/layout.tsx → Providers → here). Every client
  // consumer reads the theme from here rather than from `process.env`, which would be the build theme.
  theme: Theme;
  // `theme === 'dugout'`, derived once. The single client-side dugout gate.
  isDugoutTheme: boolean;
}

export const ToolContext = createContext<ToolContextValue | null>(null);

// Typed accessor — throws if used outside the provider (which never happens in the app: ToolProvider
// wraps the whole tree).
export const useToolContext = (): ToolContextValue => {
  const context = useContext(ToolContext);
  if (!context) {
    throw new Error('useToolContext must be used within a ToolProvider');
  }
  return context;
};

// `theme` is optional only so the provider still renders un-themed in isolation (test-utils/render.tsx,
// .storybook/preview.tsx); the app always supplies it, enforced by the required prop on Providers.
const ToolProvider = (props: { children: ReactNode; theme?: Theme }) => {
  const { children } = props;
  const buildTheme = process.env.NEXT_PUBLIC_THEME;
  const theme: Theme =
    props.theme ?? (isTheme(buildTheme) ? buildTheme : FALLBACK_THEME);
  const isDugoutTheme = isDugout(theme);

  // https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
  // Here state is saved in a ref, as a trick to keep it accessible in the event listeners.
  // The `{}` is the legacy pre-init sentinel — the real Fabric Canvas is created in Canvas.tsx's
  // mount effect (`setCanvas(initialiseCanvas(...))`); the cast keeps the runtime value byte-identical
  // while giving the ref an honest `Canvas` type for every consumer.
  const [canvas, _setCanvas] = useState<FabricCanvas>(
    {} as unknown as FabricCanvas,
  );
  const canvasStateRef = useRef<FabricCanvas>(canvas);
  const setCanvas = (canvas: FabricCanvas) => {
    canvasStateRef.current = canvas;
    _setCanvas(canvas);
    // Test-only handle for the golden-capture harness, exposed ONLY when the URL carries `?__harness=1`
    // (a flag no real embed ever sets). It lets the capture driver call `canvas.toSVG()` in page context
    // to freeze the dugout SVG oracle — dugout's save posts `img_data` (PNG), not `drawing_SVG`, so the
    // SVG cannot come back over the postMessage the way it does for the other themes. Inert (never
    // assigned) in every real deployment, so runtime + rendered output stay byte-identical there.
    if (
      typeof window !== 'undefined' &&
      window.location.search.includes('__harness=1')
    ) {
      // eslint-disable-next-line unicorn/no-global-object-property-assignment -- deliberate test-only handle; guarded by ?__harness=1 so it is never assigned in a real deploy
      (window as unknown as { __canvas?: FabricCanvas }).__canvas = canvas;
    }
  };

  // Scale (zoom factor) — identity (1) until a field loads and `setScale` writes the real factor.
  const [scale, _setScale] = useState(1);
  const scaleStateRef = useRef(scale);
  const setScale = (scale: number) => {
    scaleStateRef.current = scale;
    _setScale(scale);
  };

  // `{}` is the legacy pre-init sentinel (truthy → mobile-first paint until the mount effect sets the
  // real value from `innerWidth < 930`); the cast keeps the runtime value byte-identical while typing
  // the ref boolean.
  const [isMobile, _setIsMobile] = useState<boolean>({} as unknown as boolean);
  const isMobileStateRef = useRef<boolean>(isMobile);
  const setIsMobile = (isMobile: boolean) => {
    isMobileStateRef.current = isMobile;
    _setIsMobile(isMobile);
  };

  // same is necessary for the line type state
  const [storeLineType, _setStoreLineType] = useState('');
  const storeLineTypeStateRef = useRef(storeLineType);
  const setStoreLineType = (type: string) => {
    storeLineTypeStateRef.current = type;
    _setStoreLineType(type);
  };

  const [canvasRef, setCanvasRef] =
    useState<RefObject<HTMLCanvasElement | null> | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [fieldTypes, setFieldTypes] = useState<ItemType[]>([]);
  const [toolTypes, setToolTypes] = useState<ItemType[]>([]);
  const [lineTypes, setLineTypes] = useState<ItemType[]>([]);
  const [shirtTypes, setShirtTypes] = useState<ItemType[]>([]);
  const [goalTypes, setGoalTypes] = useState<ItemType[]>([]);
  const [itemTypes, setItemTypes] = useState<ItemType[]>([]);
  const [coneTypes, setConeTypes] = useState<ItemType[]>([]);
  const [defaultTypes, setDefaultTypes] = useState<ItemType[]>([]);
  const [fieldSelected, setFieldSelected] = useState<ItemType | null>(null);
  const [fieldDimensions, setFieldDimensions] = useState<number[]>([]);
  const [lineSelected, setLineSelected] = useState<string | null>(null);
  // Dugout has no in-tool shape toggle, so a new drawing starts in the Dugout default (a flippable
  // constant); an imported drawing's own `iconType` overrides this later. Identical for every other
  // theme, where the default is 'defaultIcon' regardless.
  // Safe as a useState initializer (rather than a mount effect) because `theme` is resolved on the
  // server and arrives as a prop, so the server render and the hydrating client render agree.
  const [iconType, setIconType] = useState<string>(
    isDugoutTheme ? DUGOUT_DEFAULT_ICON_TYPE : 'defaultIcon',
  );
  const [showCancelOverlay, setShowCancelOverlay] = useState(false);
  const [isIndoor, setIsIndoor] = useState(false);
  const [storeFieldType, setStoreFieldType] = useState('');

  const [storeIconType, setStoreIconType] = useState('defaultIcon');
  const [storeSelection, setStoreSelection] = useState<FabricObject | null>(
    null,
  );
  const [storeCollection, setStoreCollection] = useState<FabricObject[]>([]);
  const [storePitch, setStorePitch] = useState('');

  const [parentUrl, setParentUrl] = useState('');
  const [isMenuActive, setIsMenuActive] = useState(false);

  const [enableResetButton, setEnableResetButton] = useState(false);
  const [preventClickBeforeScroll, setPreventClickBeforeScroll] =
    useState(false);

  const [isDrawingLine, setIsDrawingLine] = useState(false);

  const [isIndoorActive, setIsIndoorActive] = useState(false);
  const [drawingType, setDrawingType] = useState<string | false>(false);
  const [reloadField, setReloadField] = useState(false);

  const storeAddItem = (item: FabricObject) => {
    setStoreCollection((previous) => [...previous, item]);
  };

  return (
    <ToolContext.Provider
      value={{
        theme,
        isDugoutTheme,
        scaleStateRef,
        setScale,
        canvasStateRef,
        setCanvas,
        canvasRef,
        setCanvasRef,
        selection,
        setSelection,
        fieldTypes,
        setFieldTypes,
        fieldDimensions,
        setFieldDimensions,
        toolTypes,
        setToolTypes,
        lineTypes,
        setLineTypes,
        shirtTypes,
        setShirtTypes,
        goalTypes,
        setGoalTypes,
        itemTypes,
        setItemTypes,
        coneTypes,
        setConeTypes,
        defaultTypes,
        setDefaultTypes,
        fieldSelected,
        setFieldSelected,
        lineSelected,
        setLineSelected,
        iconType,
        setIconType,

        showCancelOverlay,
        setShowCancelOverlay,
        isIndoor,
        setIsIndoor,

        storeFieldType,
        setStoreFieldType,

        setStoreLineType,
        storeLineTypeStateRef,

        storeIconType,
        setStoreIconType,
        storeSelection,
        setStoreSelection,
        storeCollection,
        setStoreCollection,
        storePitch,
        setStorePitch,

        parentUrl,
        setParentUrl,

        storeAddItem,
        isMenuActive,
        setIsMenuActive,
        isMobileStateRef,
        setIsMobile,
        enableResetButton,
        setEnableResetButton,
        preventClickBeforeScroll,
        setPreventClickBeforeScroll,
        isDrawingLine,
        setIsDrawingLine,

        isIndoorActive,
        setIsIndoorActive,
        drawingType,
        setDrawingType,

        reloadField,
        setReloadField,
      }}
    >
      {children}
    </ToolContext.Provider>
  );
};

export default ToolProvider;
